import DOMPurify from 'dompurify';
import { readVideoComments, videoCommentFrames } from './videoComments';

export interface MessageAttachment {
  id?: number;
  url: string;
  name: string;
  previewUrl?: string;
}

const IMAGE_NAME = /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i;
const LOCAL_UPLOAD_HOSTS = new Set(['tab', 'tab.myxa.tv', 'skrepka.myxa.tv']);

function mediaUrlKey(value?: string | null): string {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.href);
    // Old comments can retain the TAB hostname after opening the same file
    // through another supported address. Never merge unrelated remote hosts.
    if (['http:', 'https:'].includes(url.protocol) && url.pathname.startsWith('/uploads/')
      && (LOCAL_UPLOAD_HOSTS.has(url.hostname) || url.origin === window.location.origin)) {
      return `studio-upload:${decodeURI(url.pathname)}${url.search}`;
    }
    return url.href;
  } catch {
    return value;
  }
}

function removeImageAndEmptyWrappers(image: HTMLImageElement, container: HTMLElement) {
  let parent = image.parentElement;
  image.remove();
  while (parent && parent !== container && /^(A|P|DIV|SPAN)$/.test(parent.tagName)
    && !(parent.textContent || '').trim() && !parent.querySelector('img, video, audio, iframe')
    && [...parent.children].every(child => child.tagName === 'BR')) {
    const next = parent.parentElement;
    parent.remove();
    parent = next;
  }
}

/** Present each attachment once; do not change stored messages or files. */
export function messageAttachmentPresentation(html?: string | null, files: readonly MessageAttachment[] = []) {
  const content = document.createElement('div');
  content.innerHTML = DOMPurify.sanitize(html ?? '');
  const imageFiles = files.filter(file => IMAGE_NAME.test(file.name));

  content.querySelectorAll<HTMLImageElement>('img:not([data-attachment-id])').forEach(image => {
    const source = mediaUrlKey(image.getAttribute('src'));
    const original = mediaUrlKey(image.closest('a')?.getAttribute('href'));
    // Legacy task comments have a thumbnail in HTML and the original in the
    // attachment list (without IDs). Keep the original's normal image renderer.
    if (imageFiles.some(file => (original && original === mediaUrlKey(file.url))
      || (source && (source === mediaUrlKey(file.url) || source === mediaUrlKey(file.previewUrl))))) {
      removeImageAndEmptyWrappers(image, content);
    }
  });

  // New editor images have an explicit attachment ID and retain their position
  // among paragraphs. Use the full browser-compatible image, not its thumbnail.
  content.querySelectorAll<HTMLImageElement>('img[data-attachment-id]').forEach(image => {
    if (image.hasAttribute('data-pending')) return;
    const source = mediaUrlKey(image.getAttribute('src'));
    const original = mediaUrlKey(image.closest('a')?.getAttribute('href'));
    const file = imageFiles.find(file => (file.id !== undefined && file.id === Number(image.dataset.attachmentId))
      || (original && original === mediaUrlKey(file.url))
      || (source && (source === mediaUrlKey(file.url) || source === mediaUrlKey(file.previewUrl))));
    if (file) image.setAttribute('src', file.url);
  });

  const inlineUrls = new Set<string>();
  const inlineIds = new Set<number>();
  content.querySelectorAll<HTMLElement>('img[src], video[src], audio[src]').forEach(media => {
    const source = media.getAttribute('src');
    if (!source || source.startsWith('blob:') || media.hasAttribute('data-pending')) return;
    inlineUrls.add(mediaUrlKey(source));
    const original = media.closest('a')?.getAttribute('href');
    if (original) inlineUrls.add(mediaUrlKey(original));
    const id = Number(media.dataset.attachmentId);
    if (Number.isSafeInteger(id) && id > 0) inlineIds.add(id);
    readVideoComments(media.dataset.videoComments).forEach(comment => {
      videoCommentFrames(comment).forEach(frame => {
        inlineUrls.add(mediaUrlKey(frame.imageUrl));
        inlineIds.add(frame.attachmentId);
      });
      if (comment.voiceUrl) inlineUrls.add(mediaUrlKey(comment.voiceUrl));
      if (comment.voiceAttachmentId) inlineIds.add(comment.voiceAttachmentId);
    });
  });

  return {
    html: content.innerHTML,
    files: files.filter(file => !inlineUrls.has(mediaUrlKey(file.url))
      && !(/\.(png|jpe?g|gif|webp|bmp|svg|avif|tiff?)$/i.test(file.name) && file.previewUrl && inlineUrls.has(mediaUrlKey(file.previewUrl)))
      && !(file.id !== undefined && inlineIds.has(file.id))),
  };
}
